<template>
    <v-dialog v-model="modelValue" max-width="500">
        <v-card>
            <v-card-title class="d-flex align-center justify-space-between pr-2">
                Activity Sessions
                <v-btn icon="mdi-close" variant="text" size="small" @click="modelValue = false" />
            </v-card-title>

            <v-card-text>
                <div v-if="loading" class="text-center py-6">
                    <v-progress-circular indeterminate color="primary" />
                </div>

                <template v-else>
                    <div v-if="!upcomingActivity">
                        <!-- Recently DONE within the grace period window: spa is still warm, next session skips prep -->
                        <template v-if="isResettableGracePeriod">
                            <div class="text-overline text-medium-emphasis mb-2">Grace Period Active</div>
                            <v-card variant="outlined">
                                <v-card-text>
                                    <div class="text-body-2 text-medium-emphasis">
                                        The spa is still warm from
                                        {{
                                            gracePeriodActivities.length > 1
                                                ? `${gracePeriodActivities.length} recently completed sessions`
                                                : 'a recently completed session'
                                        }}, so the next session will skip its warm-up.
                                    </div>
                                </v-card-text>

                                <v-divider />

                                <v-card-actions class="pa-2">
                                    <template v-if="activityControlsEnabled">
                                        <v-spacer />
                                        <ResetGracePeriodButton :loading="resettingGracePeriod" @click="resetGracePeriod" />
                                    </template>
                                    <template v-else>
                                        <v-chip size="x-small" variant="tonal" color="grey" prepend-icon="mdi-eye-outline"> Read-only in production </v-chip>
                                    </template>
                                </v-card-actions>
                            </v-card>
                        </template>

                        <div v-else class="text-medium-emphasis text-body-2 py-2">No active or upcoming session found.</div>
                    </div>

                    <template v-else>
                        <div class="text-overline text-medium-emphasis mb-2">
                            {{ isRunning ? 'Current Session' : isGracePeriod ? 'Grace Period Active' : 'Next Planned Session' }}
                        </div>

                        <v-card variant="outlined">
                            <v-card-text>
                                <div class="d-flex align-center gap-2 mb-3">
                                    <v-chip :color="statusColor" size="small" label>{{ statusLabel }}</v-chip>
                                    <span class="text-caption text-medium-emphasis">{{ sessionType }}</span>
                                </div>

                                <div v-if="activityName" class="font-weight-medium mb-2">{{ activityName }}</div>

                                <div class="text-body-2 d-flex flex-column gap-1">
                                    <div v-if="prepStartLabel">
                                        <v-icon icon="mdi-clock-fast" size="14" class="mr-1" />
                                        {{ prepStartLabel }}
                                    </div>

                                    <div v-if="startTimeInfo">
                                        <v-icon :icon="startTimeInfo.icon" size="14" class="mr-1" />
                                        {{ startTimeInfo.text }}
                                    </div>

                                    <div v-if="activityEndAt">
                                        <v-icon icon="mdi-clock-end" size="14" class="mr-1" />
                                        <span v-if="statusId === ActivityStatusV1IdEnum.InProgress">
                                            Ends {{ formatDistanceToNow(new Date(activityEndAt), { addSuffix: true }) }}
                                            <span class="text-medium-emphasis">({{ format(new Date(activityEndAt), 'h:mm a') }})</span>
                                        </span>
                                        <span v-else> Ends at {{ format(new Date(activityEndAt), 'h:mm a') }} </span>
                                    </div>

                                    <div>
                                        <v-icon icon="mdi-timer-outline" size="14" class="mr-1" />
                                        Duration: {{ durationLabel }}
                                        <span v-if="(upcomingActivity?.prepDurationInSeconds ?? 0) > 0" class="text-medium-emphasis">
                                            (+ {{ Math.round((upcomingActivity?.prepDurationInSeconds ?? 0) / 60) }} min prep)
                                        </span>
                                    </div>

                                    <div v-if="setPointLabel">
                                        <v-icon icon="mdi-thermometer" size="14" class="mr-1" />
                                        Setpoint: {{ setPointLabel }}
                                    </div>

                                    <div v-if="currentTempLabel">
                                        <v-icon icon="mdi-thermometer-lines" size="14" class="mr-1" />
                                        Current Temp: {{ currentTempLabel }}
                                    </div>

                                    <div v-if="isResettableGracePeriod" class="text-medium-emphasis">
                                        <v-icon icon="mdi-fire" size="14" class="mr-1" color="orange" />
                                        Spa is still warm from a recent session — this session will skip its warm-up.
                                    </div>
                                </div>
                            </v-card-text>

                            <v-divider />

                            <v-card-actions class="flex-wrap gap-1 pa-2">
                                <template v-if="activityControlsEnabled">
                                    <!-- Pending update banner -->
                                    <template v-if="pendingUpdate">
                                        <v-chip size="small" color="orange" variant="tonal" prepend-icon="mdi-clock-outline" class="mb-1 w-100">
                                            Waiting for the spa to apply the change…
                                        </v-chip>
                                    </template>

                                    <!-- PREPARING: skip the remaining prep and start the session now -->
                                    <template v-if="isPreparing">
                                        <v-tooltip text="Skips the remaining prep phase and starts the session right now" location="top">
                                            <template #activator="{ props: tp }">
                                                <v-btn
                                                    v-bind="tp"
                                                    size="small"
                                                    variant="flat"
                                                    color="green"
                                                    prepend-icon="mdi-play-circle"
                                                    :loading="updating"
                                                    :disabled="pendingUpdate"
                                                    @click="startNow"
                                                >
                                                    Start Now
                                                </v-btn>
                                            </template>
                                        </v-tooltip>
                                    </template>

                                    <!-- Testing minute shortcuts — PREPARING: "Start in"; IN_PROGRESS: "Finish in" -->
                                    <template v-if="testingShortcut">
                                        <v-divider vertical class="mx-1" />
                                        <v-chip size="x-small" variant="outlined" color="orange" prepend-icon="mdi-flask" class="mr-1">Testing</v-chip>
                                        <span class="text-caption text-medium-emphasis mr-1">{{ testingShortcut.label }}</span>
                                        <v-btn
                                            v-for="min in TESTING_MINUTES"
                                            :key="min"
                                            size="small"
                                            variant="outlined"
                                            color="orange"
                                            :title="testingShortcut.title(min)"
                                            :loading="updating"
                                            :disabled="pendingUpdate"
                                            @click="testingShortcut.action(min)"
                                        >
                                            {{ min }} min
                                        </v-btn>
                                    </template>

                                    <!-- PREPARING, IN_PROGRESS or IN_GRACE_PERIOD: stop -->
                                    <template v-if="isRunning || isGracePeriod">
                                        <v-spacer />
                                        <v-btn size="small" variant="tonal" color="red" prepend-icon="mdi-stop-circle" @click="showStopConfirm = true">
                                            Stop Session
                                        </v-btn>
                                    </template>

                                    <!-- Next planned session, but spa is still warm from a recent session: offer to reset -->
                                    <template v-if="isResettableGracePeriod && !pendingUpdate">
                                        <v-spacer />
                                        <ResetGracePeriodButton :loading="resettingGracePeriod" @click="resetGracePeriod" />
                                    </template>
                                </template>

                                <template v-else>
                                    <v-chip size="x-small" variant="tonal" color="grey" prepend-icon="mdi-eye-outline"> Read-only in production </v-chip>
                                </template>
                            </v-card-actions>
                        </v-card>
                    </template>
                </template>
            </v-card-text>

            <v-card-actions>
                <v-btn variant="text" size="small" prepend-icon="mdi-refresh" :loading="loading" @click="fetchUpcomingActivities"> Refresh </v-btn>
                <v-chip v-if="lastFetchedAt" size="x-small" variant="tonal" color="grey" prepend-icon="mdi-clock-outline" class="ml-1">
                    {{ format(lastFetchedAt, 'h:mm:ss a') }}
                </v-chip>
                <v-spacer />
                <v-btn variant="text" @click="modelValue = false">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Stop Confirm Dialog -->
    <v-dialog v-model="showStopConfirm" max-width="440">
        <v-card>
            <v-card-title>Stop this session?</v-card-title>
            <v-card-text>
                <p>
                    Stopping will <strong>immediately signal the spa to end the current activity</strong>. Any jets or systems started by this session will
                    switch off right away.
                </p>
                <p v-if="upcomingActivity?.smartRoutineId" class="mt-3 text-body-2 text-medium-emphasis">
                    The routine itself is not disabled — the next planned session will still run as scheduled.
                </p>

                <v-label class="mt-4 mb-1 text-body-2">Reason</v-label>
                <v-btn-toggle v-model="stopReason" mandatory density="compact" variant="outlined" divided class="w-100">
                    <v-btn value="cancelled" size="small" class="flex-1-1">Cancelled</v-btn>
                    <v-btn value="completed" size="small" class="flex-1-1">Completed</v-btn>
                    <v-btn value="failed" size="small" class="flex-1-1">Failed</v-btn>
                </v-btn-toggle>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="cancelStop">Cancel</v-btn>
                <v-btn color="red" variant="tonal" :loading="stopping" @click="stopSession"> Yes, Stop Session </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { useApi } from '@/api'
import ResetGracePeriodButton from '@/components/activities/ResetGracePeriodButton.vue'
import { formatTempC, handleApiError } from '@/lib/utils'
import { fetchUpcomingActivityDetail, type UpcomingActivityDetail } from '@/lib/sessionActivity'
import { ActivityStatusV1IdEnum, StopSessionV1ReasonEnum, type GetNamedActivitySchemaV1 } from '@geckoal/gecko-api-client'
import { format, formatDistanceToNow } from 'date-fns'
import { computed, ref, watch } from 'vue'

const activityControlsEnabled = import.meta.env.VITE_ENABLE_ACTIVITY_CONTROLS === 'true'

const props = defineProps<{
    accountId: number
    vesselId: number
    /** Latest known water temperature reading for the vessel, in Celsius (e.g. vessel.status.discElements.tempC). */
    currentTempC?: number
}>()
const emit = defineEmits<{
    'session-updated': []
}>()

const modelValue = defineModel<boolean>({ required: true })

// Every activity API call is scoped to the same account + vessel
const vesselParams = computed(() => ({ accountId: props.accountId, vesselId: props.vesselId }))

const TESTING_MINUTES = [1, 2, 5]

const loading = ref(false)
const stopping = ref(false)
const updating = ref(false)
const resettingGracePeriod = ref(false)
const pendingUpdate = ref(false)
const showStopConfirm = ref(false)
const stopReason = ref<StopSessionV1ReasonEnum>(StopSessionV1ReasonEnum.Cancelled)
const upcomingActivity = ref<UpcomingActivityDetail | null>(null)
const gracePeriodActivities = ref<GetNamedActivitySchemaV1[]>([])
const lastFetchedAt = ref<Date | null>(null)

const statusId = computed(() => upcomingActivity.value?.status.id)

const STATUS_MAP: Record<string, { color: string; label: string; running: boolean }> = {
    [ActivityStatusV1IdEnum.InProgress]: { color: 'green', label: 'In Progress', running: true },
    [ActivityStatusV1IdEnum.Preparing]: { color: 'blue', label: 'Preparing', running: true },
    [ActivityStatusV1IdEnum.Idle]: { color: 'grey', label: 'Planned', running: false },
    [ActivityStatusV1IdEnum.InGracePeriod]: { color: 'orange', label: 'Grace Period', running: false }
}

const statusInfo = computed(() => STATUS_MAP[statusId.value ?? ''] ?? { color: 'grey', label: 'Idle', running: false })

const isRunning = computed(() => statusInfo.value.running)
const isGracePeriod = computed(() => statusId.value === ActivityStatusV1IdEnum.InGracePeriod)
const statusColor = computed(() => statusInfo.value.color)
const statusLabel = computed(() => statusInfo.value.label)

const sessionType = computed(() => (upcomingActivity.value?.smartRoutineId ? 'Smart Routine' : 'Impromptu Session'))

// The API types name/endAt as empty interfaces but are string | null at runtime
const activityName = computed(() => upcomingActivity.value?.name as unknown as string | null)
const activityEndAt = computed(() => upcomingActivity.value?.endAt as unknown as string | null)

const startTimeInfo = computed(() => {
    const start = upcomingActivity.value?.startAt
    if (!start) return null
    if (statusId.value === ActivityStatusV1IdEnum.InProgress) return { icon: 'mdi-clock-start', text: `Started ${format(start, 'EEE MMM d · h:mm a')}` }
    if (statusId.value === ActivityStatusV1IdEnum.Preparing)
        return { icon: 'mdi-clock-start', text: `Session starts at ${format(start, 'h:mm a')} (${formatDistanceToNow(start, { addSuffix: true })})` }
    if (statusId.value === ActivityStatusV1IdEnum.Idle) return { icon: 'mdi-calendar-clock', text: `Planned for ${format(start, 'EEE MMM d · h:mm a')}` }

    return null
})

// When startAt/prepDurationInSeconds are set, prep begins prepDurationInSeconds before the session's actual start.
const prepStartAt = computed(() => {
    const start = upcomingActivity.value?.startAt
    const prepSeconds = upcomingActivity.value?.prepDurationInSeconds ?? 0
    if (!start || prepSeconds <= 0) return null
    return new Date(new Date(start).getTime() - prepSeconds * 1000)
})

const prepStartLabel = computed(() => {
    if (!prepStartAt.value) return null
    if (statusId.value === ActivityStatusV1IdEnum.Preparing) return `Prep started at ${format(prepStartAt.value, 'h:mm a')}`
    if (statusId.value === ActivityStatusV1IdEnum.Idle) return `Prep starts at ${format(prepStartAt.value, 'h:mm a')}`
    return null
})

const durationLabel = computed(() => {
    const secs = upcomingActivity.value?.durationInSeconds ?? 0
    const mins = Math.round(secs / 60)
    return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`
})

// Only meaningful once a smart routine session has a target temperature — absent for manual sessions.
const setPointLabel = computed(() => {
    const setPointC = upcomingActivity.value?.setPointC
    return setPointC == null ? null : formatTempC(setPointC)
})

const currentTempLabel = computed(() => (props.currentTempC == null ? null : formatTempC(props.currentTempC)))

const isPreparing = computed(() => statusId.value === ActivityStatusV1IdEnum.Preparing)

// True when a recently-DONE activity is still within the grace period window (spa is warm, next session
// would skip prep) and nothing is actively running — i.e. there's no session, or only a next-planned IDLE one.
// In that state admins can reset the grace period so the next session runs a full warm-up.
const isResettableGracePeriod = computed(
    () => gracePeriodActivities.value.length > 0 && (!upcomingActivity.value || statusId.value === ActivityStatusV1IdEnum.Idle)
)

const plural = (min: number) => (min > 1 ? 's' : '')

// Testing-only minute shortcuts. PREPARING shortens prep so the session starts in N min;
// IN_PROGRESS shortens endAt so the session ends in N min. Null for any other status.
type TestingShortcut = { label: string; title: (min: number) => string; action: (min: number) => void }
const testingShortcut = computed<TestingShortcut | null>(() => {
    if (isPreparing.value)
        return {
            label: 'Start in',
            title: (min) => `Set ${min} minute${plural(min)} of prep remaining so the session starts in ${min} min`,
            action: setRemainingPrep
        }
    if (statusId.value === ActivityStatusV1IdEnum.InProgress)
        return {
            label: 'Finish in',
            title: (min) => `Shorten session so it ends in ${min} minute${plural(min)} from now`,
            action: finishIn
        }
    return null
})

async function fetchUpcomingActivities(clearPending = true) {
    loading.value = true
    await handleApiError(async () => {
        const [upcoming, gracePeriod] = await Promise.all([
            fetchUpcomingActivityDetail(vesselParams.value.accountId, vesselParams.value.vesselId),
            useApi()
                .getGracePeriodActivitiesV1(vesselParams.value)
                .catch(() => [])
        ])
        upcomingActivity.value = upcoming ?? null
        gracePeriodActivities.value = gracePeriod ?? []
        lastFetchedAt.value = new Date()
        // A pending change only applies to an active (PREPARING/IN_PROGRESS) session. Once the
        // session is idle/grace/done there's nothing left for the spa to apply, so always clear
        // it — otherwise the "Waiting…" banner sticks around (and suppresses the Reset button)
        // long after the action completed.
        const fetchedStatusId = upcoming?.status?.id
        const sessionIsActive = fetchedStatusId === ActivityStatusV1IdEnum.Preparing || fetchedStatusId === ActivityStatusV1IdEnum.InProgress
        if (clearPending || !sessionIsActive) pendingUpdate.value = false
    })
    loading.value = false
}

async function updateActivity(body: { startAt?: Date; endAt?: Date; prepDurationInSeconds?: number }) {
    const activityId = upcomingActivity.value?.activityId
    if (!activityId) return
    updating.value = true
    await handleApiError(async () => {
        await useApi().putVesselActivityV1({
            ...vesselParams.value,
            activityId,
            putVesselActivityBodyV1: body
        })
        await fetchUpcomingActivities(false) // refresh data but keep buttons locked
        pendingUpdate.value = true
        emit('session-updated')
    })
    updating.value = false
}

// Skips all remaining prep: sets startAt to now and clears prepDurationInSeconds atomically
async function startNow() {
    await updateActivity({ startAt: new Date(), prepDurationInSeconds: 0 })
}

// IN_PROGRESS only: shortens the session by setting endAt to now + minutes (admin only on the API side).
async function finishIn(minutes: number) {
    await updateActivity({ endAt: new Date(Date.now() + minutes * 60 * 1000) })
}

// PREPARING only: moves startAt forward so exactly `minutes` of prep remain from now,
// and aligns prepDurationInSeconds so the DB stays consistent with the new window.
async function setRemainingPrep(minutes: number) {
    const seconds = minutes * 60
    await updateActivity({
        startAt: new Date(Date.now() + seconds * 1000),
        prepDurationInSeconds: seconds
    })
}

// Deletes every DONE activity still inside the grace window in one go, so the spa is no longer
// considered warm and the next session runs a full warm-up (otherwise leftover DONE activities
// would keep the grace period active and the button would reappear).
async function resetGracePeriod() {
    const ids = gracePeriodActivities.value.map((a) => a.activityId)
    if (ids.length === 0) return
    resettingGracePeriod.value = true
    await handleApiError(async () => {
        await Promise.all(ids.map((activityId) => useApi().deleteVesselActivityV1({ ...vesselParams.value, activityId })))
        await fetchUpcomingActivities()
        emit('session-updated')
    })
    resettingGracePeriod.value = false
}

function cancelStop() {
    showStopConfirm.value = false
    stopReason.value = StopSessionV1ReasonEnum.Cancelled
}

async function stopSession() {
    const activityId = upcomingActivity.value?.activityId
    if (!activityId) return
    stopping.value = true
    await handleApiError(async () => {
        await useApi().stopSessionV1({
            ...vesselParams.value,
            activityId,
            reason: stopReason.value
        })
        cancelStop()
        await fetchUpcomingActivities()
        emit('session-updated')
    })
    stopping.value = false
}

// Watch the getter (not the defineModel ref directly) so the callback reliably fires when the
// parent toggles the v-model open/closed — this is what triggers the fresh fetch on each open.
watch(
    () => modelValue.value,
    (open) => {
        if (open) {
            fetchUpcomingActivities(false) // preserve pendingUpdate — only Refresh should clear it
        } else {
            upcomingActivity.value = null
            gracePeriodActivities.value = []
            lastFetchedAt.value = null
            // intentionally NOT resetting pendingUpdate here:
            // the Lambda may still be processing the last action
        }
    },
    { immediate: true }
)
</script>
